import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from './IconButton';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Pasek akcji na dole okna. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Blokuje zamkniecie klawiszem Escape i klikiem w tlo (operacje nieodwracalne). */
  dismissible?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
} as const;

/**
 * Okno modalne oparte na natywnym elemencie `<dialog>`.
 *
 * Wybor natywnego elementu jest swiadomy: przegladarka sama pilnuje pulapki
 * fokusu, warstwy nadrzednej i zamykania klawiszem Escape. Wlasna implementacja
 * musialaby to wszystko odtworzyc, zwykle gorzej.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // `cancel` leci przy Escape — przechwytujemy, zeby stan w Reakcie
    // nie rozjechal sie z faktycznym stanem elementu.
    const handleCancel = (event: Event) => {
      event.preventDefault();
      if (dismissible) onClose();
    };

    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [dismissible, onClose]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="modal-title"
      onClick={(event) => {
        // Klik w tlo: cel zdarzenia to sam `<dialog>`, a nie jego zawartosc.
        if (dismissible && event.target === dialogRef.current) onClose();
      }}
      className={cn(
        'bg-surface text-fg m-auto w-[calc(100%-2rem)] rounded-2xl p-0 shadow-xl',
        'backdrop:bg-overlay backdrop:backdrop-blur-[2px]',
        sizeClasses[size],
        className,
      )}
    >
      <div className="flex items-start justify-between gap-4 p-5 pb-0">
        <div className="min-w-0">
          <h2 id="modal-title" className="text-fg-strong text-lg font-bold tracking-[-0.02em]">
            {title}
          </h2>
          {description && <p className="text-fg-muted mt-1 text-sm">{description}</p>}
        </div>

        {dismissible && (
          <IconButton icon={<X />} label="Zamknij" size="sm" onClick={onClose} className="-mt-1" />
        )}
      </div>

      {children && <div className="p-5 text-sm">{children}</div>}

      {footer && (
        <div className="border-border flex items-center justify-end gap-3 border-t px-5 py-4">
          {footer}
        </div>
      )}
    </dialog>
  );
}
